import { useCallback, useMemo, useState } from 'react'
import { format, addMonths } from 'date-fns'

import { DaysAvailabilities } from './types'
import { findAvailability } from './dayAvailability'

type NumberOfMonths = 1 | 2
type VisibleMonths = [string, string?]

const MONTH_FORMAT = 'yyyy-MM'

function buildVisibleMonths(
  visibleDate: Date,
  numberOfMonths: NumberOfMonths,
): VisibleMonths {
  const visibleMonths: VisibleMonths = [format(visibleDate, MONTH_FORMAT)]

  if (numberOfMonths === 2) {
    visibleMonths.push(format(addMonths(visibleDate, 1), MONTH_FORMAT))
  }

  return visibleMonths
}

export function useVisibleMonths(
  startDate: Date | null,
  numberOfMonths: NumberOfMonths,
) {
  const [visibleMonths, setVisibleMonths] = useState<VisibleMonths>(() => {
    return buildVisibleMonths(startDate || new Date(), numberOfMonths)
  })

  const updateCurrentMonth = useCallback(
    (currentMonth: Date) => {
      setVisibleMonths(buildVisibleMonths(currentMonth, numberOfMonths))
    },
    [numberOfMonths],
  )

  return [visibleMonths, updateCurrentMonth] as const
}

export default function useDiscountsVisible(
  startDate: Date | null,
  numberOfMonths: NumberOfMonths,
  daysData: DaysAvailabilities,
) {
  const [visibleMonths, updateCurrentMonth] = useVisibleMonths(
    startDate,
    numberOfMonths,
  )

  const discountsVisible = useMemo(() => {
    if (!daysData) {
      return false
    }

    if (startDate) {
      const startDateData = findAvailability({ day: startDate, daysData })
      return Boolean(startDateData?.discount)
    }

    const today = format(new Date(), 'yyyy-MM-dd')
    return Object.keys(daysData).some((date) => {
      if (date < today) {
        // We don't need to check dates in the past or after the selected
        // start date:
        return false
      }

      const dateData = daysData[date]
      return (
        dateData?.discount &&
        dateData?.available &&
        visibleMonths.some((month) => date.startsWith(month))
      )
    })
  }, [startDate, visibleMonths, daysData])

  return { discountsVisible, updateCurrentMonth }
}
